/**
 * Export Table Data to Excel - CLOUDBURST REPORTS
 * ✅ FIXED: Top 3 Metadata rows (Report Name, From Date, To Date) are now BOLD.
 */

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { 
  transformDataForExcel, 
  transformMISDataForExcel, 
  cloudburstMISConfig,
  getTimestamp 
} from "./excelUtilsCloudburst";

// Status Formatting (Red/Yellow/Green)
const applyStatusFormatting = (worksheet, columns) => {
  let subIndex = -1, notSubIndex = -1;
  columns.forEach((col, i) => {
    if (col.label === 'Report Submitted') subIndex = i + 1;
    if (col.label === 'Report Not Submitted') notSubIndex = i + 1;
  });

  if (subIndex === -1 || notSubIndex === -1) return;

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const submitted = row.getCell(subIndex).value;
    const notSubmitted = row.getCell(notSubIndex).value;

    let color = 'FFFFFF00'; // Yellow
    if (submitted == 0) color = 'FFFF0000'; // Red
    else if (notSubmitted == 0) color = 'FF00FF00'; // Green

    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
    });
  });
};

// Date cleaning helper (DD-MM-YYYY)
const formatHeaderDate = (dateVal) => {
  if (!dateVal) return "NA";
  if (typeof dateVal === "string") {
    return dateVal.replace(/\//g, "-"); 
  }
  return dateVal;
};

export const generateCloudburstExcel = async (reportData) => {
  const { data, toolName, selectedView, filename, columnOrder } = reportData;

  // 1. STATUS REPORT
  if (toolName === 'cloudburst_daily_report_status' || toolName === 'cloudburstdailyreportstatus') {
     const { columns, transformedData } = transformDataForExcel(data, columnOrder || ['dist_name', 'total_block_count', 'report_submitted', 'report_not_submitted']);
     const workbook = new ExcelJS.Workbook();
     const worksheet = workbook.addWorksheet("Sheet 1");
     
     worksheet.addRow(columns.map(c => c.label));
     transformedData.forEach(r => worksheet.addRow(columns.map(c => r[c.label])));

     applyStatusFormatting(worksheet, columns);
     
     const buffer = await workbook.xlsx.writeBuffer();
     saveAs(new Blob([buffer]), `DailyCloudBurstReportStatus_${getTimestamp()}.xlsx`);
     return;
  }

  // 2. MIS REPORT
  const config = cloudburstMISConfig[selectedView] || cloudburstMISConfig.dist_date;
  const { headers, rows } = transformMISDataForExcel(data, selectedView);
  
  const fromDate = formatHeaderDate(data.from_date);
  const toDate = formatHeaderDate(data.to_date);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet 1");

  // Add the 3 Metadata Rows
  worksheet.addRow([`Report Name: ${config.title}`]);
  worksheet.addRow([`From Date: ${fromDate}`]);
  worksheet.addRow([`To Date: ${toDate}`]);
  
  // Merge Cells A to J (One Box effect)
  const totalColumns = headers.length > 0 ? headers.length : 10; 
  
  // Merge row 1, 2, and 3 across all columns
  worksheet.mergeCells(1, 1, 1, totalColumns); 
  worksheet.mergeCells(2, 1, 2, totalColumns); 
  worksheet.mergeCells(3, 1, 3, totalColumns); 

  // ✅ FIX: Style the merged cells to align left and be BOLD
  for(let i=1; i<=3; i++) {
      const cell = worksheet.getCell(i, 1);
      cell.font = { bold: true, size: 11 }; // <--- Changed to bold: true
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
  }

  // Add Headers and Data
  worksheet.addRow(headers);
  const headerRow = worksheet.getRow(4);
  headerRow.font = { bold: true }; 

  rows.forEach(r => worksheet.addRow(r));

  const buffer = await workbook.xlsx.writeBuffer();
  // Dynamic filename based on view
  let exportFilename = `MIS_${config.title.replace(/_CLOUD_BURST_REPORT/g, '')}_Cloud_Burst_Report_${getTimestamp()}.xlsx`;
  saveAs(new Blob([buffer]), exportFilename);
};